/**
 * ResizeObserver error suppression utility
 * 
 * The "ResizeObserver loop completed with undelivered notifications" error
 * is a common, harmless error that occurs when:
 * 1. ResizeObserver detects size changes
 * 2. The callback causes layout changes
 * 3. This creates more size changes than can be processed in one frame
 * 
 * This error doesn't affect functionality but can clutter console output.
 * This utility safely suppresses these errors.
 */

// Suppress ResizeObserver errors in console
export const suppressResizeObserverErrors = () => {
  // Store original console.error
  const originalConsoleError = console.error;
  
  console.error = (...args) => {
    // Check if this is a ResizeObserver error
    if (
      args[0] && 
      typeof args[0] === 'string' && 
      args[0].includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      // Silently ignore ResizeObserver errors
      return;
    }
    
    // For all other errors, use the original console.error
    originalConsoleError.apply(console, args);
  };
};

// Suppress ResizeObserver errors in error event handler
export const suppressResizeObserverErrorEvents = () => {
  window.addEventListener('error', (event) => {
    if (
      event.message && 
      event.message.includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
  });

  // Also handle unhandled promise rejections that might contain ResizeObserver errors
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason && 
      event.reason.message && 
      event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      event.preventDefault();
      return false;
    }
  });
};

// Initialize all error suppression
export const initializeErrorSuppression = () => {
  suppressResizeObserverErrors();
  suppressResizeObserverErrorEvents();
  
  // Also try to hide webpack dev server overlay if it shows ResizeObserver errors
  if (process.env.NODE_ENV === 'development') {
    const hideWebpackOverlay = () => {
      const overlay = document.getElementById('webpack-dev-server-client-overlay');
      const overlayDiv = document.getElementById('webpack-dev-server-client-overlay-div');
      
      if (overlay) {
        overlay.style.display = 'none';
      }
      if (overlayDiv) {
        overlayDiv.style.display = 'none';
      }
    };

    // Try to hide overlay after a short delay
    setTimeout(hideWebpackOverlay, 100);
    
    // Also observe for dynamic overlay creation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (
              node.id === 'webpack-dev-server-client-overlay' || 
              node.id === 'webpack-dev-server-client-overlay-div'
            ) {
              const content = node.textContent || '';
              if (content.includes('ResizeObserver loop completed with undelivered notifications')) {
                node.style.display = 'none';
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
};